/* psq-brush-quiz.js
   Petstyle24 – Bürstenberatung (Quiz)

   FIX: Cart drawer white / not updated / needs refresh
   Strategy:
   - Add via /cart/add.js
   - Fetch /cart.js (no-store)
   - If theme exposes cart-drawer.renderContents(cart): use it (best)
   - Open drawer via cartDrawer.open() if available, else hard-open (#CartDrawer / overlay / classes)
   - Fallback: fetch sections via Shopify.routes.root and replace DOM roots

   ENHANCEMENTS:
   - Button UX: show "Hinzugefügt ✓" instantly (no flicker)
   - Bubble UX: update cart count immediately via cart.json + DOM patch (works even if theme sections don't match)

   NEW (final):
   - Title + image clickable (without link styling) + correct URL always
   - Keep quiz open ONLY on browser "Back" via bfcache (NOT via sessionStorage; refresh resets)
*/

(() => {
  const roots = document.querySelectorAll('[data-psq-root]');
  if (!roots.length) return;

  // -----------------------------
  // Helpers: HTML parsing + DOM replace
  // -----------------------------
  function parseSectionHTML(html) {
    return new DOMParser().parseFromString(html || '', 'text/html');
  }

  function safeUpgrade(el) {
    try {
      if (window.customElements && typeof window.customElements.upgrade === 'function' && el) {
        window.customElements.upgrade(el);
      }
    } catch (e) {}
  }

  function replaceSectionRoot(sectionHtml, shopifySectionDomId) {
    const target = document.getElementById(shopifySectionDomId);
    if (!target) return false;

    const doc = parseSectionHTML(sectionHtml);
    const source = doc.getElementById(shopifySectionDomId);
    if (!source) return false;

    target.innerHTML = source.innerHTML;
    safeUpgrade(target);
    return true;
  }

  function replaceById(sectionHtml, targetId) {
    const target = document.getElementById(targetId);
    if (!target) return false;

    const doc = parseSectionHTML(sectionHtml);
    const source = doc.getElementById(targetId);
    if (!source) return false;

    target.innerHTML = source.innerHTML;
    safeUpgrade(target);
    return true;
  }

  function getThemeRoot() {
    const r = (window.Shopify && Shopify.routes && Shopify.routes.root) ? Shopify.routes.root : '/';
    return r.endsWith('/') ? r : (r + '/');
  }

  // -----------------------------
  // Bubble update (robust)
  // -----------------------------
  function updateCartCountBubbleFromCart(cart) {
    if (!cart || typeof cart.item_count !== 'number') return;

    const count = cart.item_count;

    const bubbleRoot = document.getElementById('cart-icon-bubble');
    if (bubbleRoot) {
      const bubble = bubbleRoot.querySelector('.cart-count-bubble');

      if (count <= 0) {
        if (bubble) bubble.remove();
        bubbleRoot.setAttribute('aria-label', 'Warenkorb (0)');
        return;
      }

      if (bubble) {
        const ariaHidden = bubble.querySelector('[aria-hidden="true"]');
        if (ariaHidden) ariaHidden.textContent = String(count);
        else bubble.textContent = String(count);

        const hidden = bubble.querySelector('.visually-hidden');
        if (hidden) hidden.textContent = `Artikel im Warenkorb: ${count}`;
      } else {
        const span = document.createElement('span');
        span.className = 'cart-count-bubble';
        span.innerHTML = `<span aria-hidden="true">${count}</span><span class="visually-hidden">Artikel im Warenkorb: ${count}</span>`;
        bubbleRoot.appendChild(span);
      }

      bubbleRoot.setAttribute('aria-label', `Warenkorb (${count})`);
    }

    const bubbleMobile = document.getElementById('cart-icon-bubble--mobile');
    if (bubbleMobile) {
      const b = bubbleMobile.querySelector('.cart-count-bubble');
      if (count <= 0) {
        if (b) b.remove();
        bubbleMobile.setAttribute('aria-label', 'Warenkorb (0)');
      } else {
        if (b) {
          const ariaHidden = b.querySelector('[aria-hidden="true"]');
          if (ariaHidden) ariaHidden.textContent = String(count);
          else b.textContent = String(count);
          const hidden = b.querySelector('.visually-hidden');
          if (hidden) hidden.textContent = `Artikel im Warenkorb: ${count}`;
        } else {
          const span = document.createElement('span');
          span.className = 'cart-count-bubble';
          span.innerHTML = `<span aria-hidden="true">${count}</span><span class="visually-hidden">Artikel im Warenkorb: ${count}</span>`;
          bubbleMobile.appendChild(span);
        }
        bubbleMobile.setAttribute('aria-label', `Warenkorb (${count})`);
      }
    }
  }

  // -----------------------------
  // Drawer open/close (robust)
  // -----------------------------
  function lockBodyScroll() {
    document.documentElement.classList.add('pst-cartdrawer-lock');
    document.body.classList.add('pst-cartdrawer-lock');
    document.body.classList.add('overflow-hidden');
  }

  function unlockBodyScroll() {
    document.documentElement.classList.remove('pst-cartdrawer-lock');
    document.body.classList.remove('pst-cartdrawer-lock');
    document.body.classList.remove('overflow-hidden');
  }

  function getDrawerEls() {
    const cartDrawerCustom = document.querySelector('cart-drawer');
    const cartDrawerId = document.getElementById('CartDrawer');
    const overlay =
      document.getElementById('CartDrawer-Overlay') ||
      document.querySelector('#CartDrawerOverlay, .drawer-overlay, .cart-drawer-overlay, [data-drawer-overlay]');

    const container =
      document.querySelector('.cart-drawer-container') ||
      (cartDrawerId ? cartDrawerId.closest('.cart-drawer-container') : null) ||
      (cartDrawerId ? cartDrawerId.closest('.drawer') : null);

    return { cartDrawerCustom, cartDrawerId, overlay, container };
  }

  function openDrawerHard() {
    const { cartDrawerCustom, cartDrawerId, overlay, container } = getDrawerEls();

    if (cartDrawerCustom && typeof cartDrawerCustom.open === 'function') {
      try { cartDrawerCustom.open(); return; } catch (e) {}
    }

    document.querySelectorAll('.cart-drawer-container').forEach(el => { el.style.visibility = 'visible'; });

    if (cartDrawerCustom) {
      cartDrawerCustom.classList.add('active', 'animate', 'is-open');
      cartDrawerCustom.setAttribute('open', '');
      safeUpgrade(cartDrawerCustom);
    }

    if (cartDrawerId) {
      cartDrawerId.classList.add('active', 'animate', 'is-open');
      cartDrawerId.setAttribute('open', '');
      safeUpgrade(cartDrawerId);
    }

    if (container) {
      container.classList.add('active', 'animate', 'is-open');
      container.style.visibility = 'visible';
      safeUpgrade(container);
    }

    if (overlay) {
      overlay.style.display = 'block';
      overlay.classList.add('active', 'is-visible');
    }

    lockBodyScroll();

    const closeBtn =
      document.querySelector('#CartDrawer .drawer__close') ||
      document.querySelector('#CartDrawer [data-drawer-close], .drawer__close, [aria-label="Close"]');
    if (closeBtn) {
      try { closeBtn.focus({ preventScroll: true }); } catch (e) {}
    }
  }

  function closeDrawerHard() {
    const { cartDrawerCustom, cartDrawerId, overlay, container } = getDrawerEls();

    if (cartDrawerCustom && typeof cartDrawerCustom.close === 'function') {
      try { cartDrawerCustom.close(); unlockBodyScroll(); return; } catch (e) {}
    }

    if (cartDrawerCustom) {
      cartDrawerCustom.classList.remove('active', 'animate', 'is-open');
      cartDrawerCustom.removeAttribute('open');
    }
    if (cartDrawerId) {
      cartDrawerId.classList.remove('active', 'animate', 'is-open');
      cartDrawerId.removeAttribute('open');
    }
    if (container) container.classList.remove('active', 'animate', 'is-open');

    if (overlay) {
      overlay.style.display = '';
      overlay.classList.remove('active', 'is-visible');
    }

    unlockBodyScroll();
  }

  function attachCloseHandlersOnce() {
    if (window.__psqDrawerCloseBound) return;
    window.__psqDrawerCloseBound = true;

    document.addEventListener('click', (e) => {
      const t = e.target;
      if (!t) return;
      if (t.closest && (
        t.closest('#CartDrawer-Overlay') ||
        t.closest('#CartDrawer .drawer__close') ||
        t.closest('[data-drawer-overlay]') ||
        t.closest('[data-drawer-close]')
      )) {
        closeDrawerHard();
      }
    }, true);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeDrawerHard();
    });
  }

  async function fetchCartJson() {
    try {
      const cartRes = await fetch('/cart.js', {
        headers: { 'Accept': 'application/json' },
        cache: 'no-store'
      });
      if (!cartRes.ok) return null;
      return await cartRes.json();
    } catch (e) {
      return null;
    }
  }

  async function refreshSectionsFallback() {
    const sectionIds = [
      'cart-drawer',
      'cart-icon-bubble',
      'cart-icon-bubble--mobile',
      'cart-free-delivery'
    ];

    const root = getThemeRoot();
    const secUrl = `${root}?sections=${encodeURIComponent(sectionIds.join(','))}`;

    const secRes = await fetch(secUrl, {
      headers: { 'Accept': 'application/json' },
      cache: 'no-store'
    });
    if (!secRes.ok) return null;

    return await secRes.json();
  }

  // -----------------------------
  // Add-to-cart + update drawer
  // -----------------------------
  async function addToCartAndUpdateDrawer(variantId) {
    if (!variantId) throw new Error('missing variantId');

    attachCloseHandlersOnce();

    const addRes = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    });
    if (!addRes.ok) throw new Error('cart add failed');

    const cart = await fetchCartJson();
    if (cart) updateCartCountBubbleFromCart(cart);

    const cartDrawerEl = document.querySelector('cart-drawer');
    if (cartDrawerEl && typeof cartDrawerEl.renderContents === 'function' && cart) {
      try {
        cartDrawerEl.renderContents(cart);
        safeUpgrade(cartDrawerEl);
        openDrawerHard();
        document.dispatchEvent(new CustomEvent('cart:updated'));
        return;
      } catch (e) {}
    }

    const sectionsJson = await refreshSectionsFallback();
    if (sectionsJson) {
      if (sectionsJson['cart-drawer']) {
        const ok =
          replaceSectionRoot(sectionsJson['cart-drawer'], 'shopify-section-cart-drawer') ||
          replaceSectionRoot(sectionsJson['cart-drawer'], 'shopify-section-mini-cart');

        if (!ok) replaceById(sectionsJson['cart-drawer'], 'CartDrawer');
      }

      if (sectionsJson['cart-icon-bubble']) {
        replaceSectionRoot(sectionsJson['cart-icon-bubble'], 'shopify-section-cart-icon-bubble');
      }
      if (sectionsJson['cart-icon-bubble--mobile']) {
        replaceSectionRoot(sectionsJson['cart-icon-bubble--mobile'], 'shopify-section-cart-icon-bubble--mobile');
      }
      if (sectionsJson['cart-free-delivery']) {
        replaceSectionRoot(sectionsJson['cart-free-delivery'], 'shopify-section-cart-free-delivery');

        const doc = parseSectionHTML(sectionsJson['cart-free-delivery']);
        const src = doc.querySelector('#shopify-section-cart-free-delivery');
        const html = src ? src.innerHTML : '';
        const p1 = document.getElementById('product-page-free-delivery');
        const p2 = document.getElementById('cart-page-free-delivery');
        if (p1 && html) p1.innerHTML = html;
        if (p2 && html) p2.innerHTML = html;
      }
    }

    openDrawerHard();
    document.dispatchEvent(new CustomEvent('cart:updated'));
  }

  // -----------------------------
  // Quiz logic
  // -----------------------------
  roots.forEach((root) => {
    let productsRaw = {};
    let variantsRaw = {};
    try {
      productsRaw = JSON.parse(root.getAttribute('data-psq-products') || '{}');
      variantsRaw = JSON.parse(root.getAttribute('data-psq-variants') || '{}');
    } catch (e) {
      return;
    }

    const sticky = root.querySelector('[data-psq-sticky]');
    const stickyStep = root.querySelector('[data-psq-sticky-step]');
    const bar = root.querySelector('[data-psq-bar]');

    const steps = ['1', '2', '3', '4', '5', 'result'];
    let stepIdx = -1;

    const state = { animal: null, size: null, coat: null, problem: null, intensity: null, skin: null };

    const BRUSH = {
      undercoater_duo: { key: 'undercoater_duo', title: 'ActiVet DUO UNDERCOATER', desc: 'Entfilzen + Unterwolle entfernen – ideal bei dichtem Doppelfell & Filzneigung.', bullets: ['2-in-1: Entfilzen + Unterfell', 'Für dichtes/lockiges/langes Fell', 'Sauberes Finish ohne Werkzeugwechsel'] },
      daily_care: { key: 'daily_care', title: 'ActiVet Daily Care', desc: 'Premium-Pflege für den Alltag – Borstenhärte passend zu Fell & Haut.', bullets: ['Sanft & regelmäßig einsetzbar', 'Entfernt lose Haare zuverlässig', 'Ideal für sensible Tiere'] },
      cg_stark: { key: 'cg_stark', title: 'ActiVet Coat Grabber STARK', desc: 'Unterwolle tief entfernen & sauber ausarbeiten – für dichtes, langes Fell.', bullets: ['Unterwolle effektiv entfernen', 'Für Langhaar & dichte Fellschichten', 'Perfekt als Finish-Tool'] },
      matt_zapper: { key: 'matt_zapper', title: 'ActiVet Matt Zapper', desc: 'Spezialist für harte Knoten & Filz – kontrolliert entfilzen bei Problemfell.', bullets: ['Löst Filzplatten & feste Knoten', 'Für verfilzungsanfällige Felltypen', 'Gezielte Vorarbeit vor dem Finish'] },
      duo_tufffinish: { key: 'duo_tufffinish', title: 'ActiVet DUO TUFFFINISHCOATER', desc: 'Kräftige Pflege + Unterfell-Finish – für regelmäßige, gründliche Routine.', bullets: ['2-in-1 Pflege + Unterfell-Finish', 'Für dichtes Fell & Unterwolle', 'Gründlich ohne „Zapper“'] },
      pro_weich: { key: 'pro_weich', title: 'ActiVet Pro Bürste WEICH', desc: 'Sanfte PRO-Allroundpflege – perfekt für kurzes/seidiges Fell & leichte Unterwolle.', bullets: ['Sehr angenehm auf der Haut', 'Für kurzes/seidiges Fell', 'Tägliche Pflege möglich'] },
      pro_stark: { key: 'pro_stark', title: 'ActiVet Pro Bürste STARK', desc: 'Kraftvolle Allroundpflege – für dichtes Fell, Unterwolle & leichte Knoten.', bullets: ['Für dichtes Deckhaar', 'Reduziert Unterwolle zuverlässig', 'Alltagstauglich & effektiv'] },
      cg_supersoft: { key: 'cg_supersoft', title: 'ActiVet SUPERSOFT Coat Grabber', desc: 'Sehr sanfte Unterfellpflege – entfernt Flaum & Schuppen bei kurzem Fell.', bullets: ['Sanftes Unterfell-Finish', 'Hilft bei Flaum & Schuppen', 'Ideal bei kurzem Fell'] },
      duo_tuffzapper: { key: 'duo_tuffzapper', title: 'ActiVet DUO TUFFZAPPERCOATER', desc: 'Entfilzen + kräftige Pflege – für sehr dichtes/lockiges Fell mit Filz.', bullets: ['2-in-1: Problemlösung + Pflege', 'Für dichte/lockige Felltypen', 'Strukturierte Routine bei Filz'] },
      duo_silcoater: { key: 'duo_silcoater', title: 'ActiVet DUO SILCOATER', desc: 'Sanfte Daily-Pflege + Unterfell-Finish – ideal für empfindliche Tiere.', bullets: ['2-in-1 für den Alltag', 'Sanft bei empfindlicher Haut', 'Leichte Unterwolle & Finish'] }
    };

    function normalize(s) { return (s || '').toLowerCase().replace(/\s+/g, ' ').trim(); }

    function setPressed(selector, value) {
      root.querySelectorAll(selector).forEach(btn => {
        const isOn = value && btn.dataset.v === value;
        btn.setAttribute('aria-pressed', isOn ? 'true' : 'false');
      });
    }

    function showErr(step, on) {
      const el = root.querySelector(`.psq__err[data-err-for="${step}"]`);
      if (!el) return;
      el.hidden = !on;
    }

    function setNextDisabled(step, disabled) {
      const panel = root.querySelector(`[data-step="${step}"]`);
      if (!panel) return;
      const next = panel.querySelector(`[data-act="next"]`);
      const res = panel.querySelector(`[data-act="result"]`);
      if (next) next.disabled = !!disabled;
      if (res) res.disabled = !!disabled;
    }

    function validateStep(step) {
      if (step === '1') {
        if (!state.animal) return false;
        if (state.animal === 'dog' && !state.size) return false;
        return true;
      }
      if (step === '2') return !!state.coat;
      if (step === '3') return !!state.problem;
      if (step === '4') return !!state.intensity;
      if (step === '5') return !!state.skin;
      return true;
    }

    function getHeaderOffsetPx() {
      const v = getComputedStyle(root).getPropertyValue('--psqHeaderOffset').trim();
      const n = parseInt(v, 10);
      return Number.isFinite(n) ? n : 0;
    }

    function scrollToQuizTop() {
      const headerOffset = getHeaderOffsetPx();
      const gap = 8;

      const y = Math.max(
        0,
        Math.round(window.pageYOffset + root.getBoundingClientRect().top - headerOffset - gap)
      );

      window.scrollTo({ top: y, behavior: 'smooth' });
    }

    function showPanel(el, doScroll = true) {
      root.querySelectorAll('.psq__panel, [data-view]').forEach(s => s.hidden = true);
      el.hidden = false;

      el.classList.remove('psq__fadeIn');
      void el.offsetWidth;
      el.classList.add('psq__fadeIn');

      if (doScroll) scrollToQuizTop();
    }

    function showStep(i) {
      stepIdx = i;
      const key = steps[i];

      if (key === 'result') {
        if (sticky) sticky.hidden = true;
        showPanel(root.querySelector('[data-step="result"]'), true);
        return;
      }

      if (sticky) sticky.hidden = false;

      if (stickyStep) stickyStep.textContent = `${i + 1}/5`;
      if (bar) bar.style.width = ((i + 1) / 5 * 100) + '%';

      showPanel(root.querySelector(`[data-step="${key}"]`), true);
      showErr(key, false);
      setNextDisabled(key, !validateStep(key));
    }

    function inferSize() {
      if (state.animal === 'cat') return '4.5';
      if (state.size === 'large') return '9';
      if (state.size === 'medium') return '9';
      if (state.size === 'small') return '4.5';
      return '4.5';
    }

    function dailyHardness() {
      if (state.skin === 'sensitive') return 'Grün';
      if (state.skin === 'normal') {
        if (state.coat === 'silky') return 'Grün';
        if (state.coat === 'short_smooth' && state.problem === 'routine') return 'Grün';
        if (state.coat === 'short_smooth' && state.problem === 'shedding') return 'Gold';
        if (state.coat === 'medium_dense' || state.coat === 'doublecoat') {
          if (state.intensity === 'intensive' || state.problem === 'undercoat') return 'Lila';
          return 'Gold';
        }
        if (state.intensity === 'gentle') return 'Grün';
        return 'Gold';
      }
      if (state.skin === 'robust') {
        if (state.coat === 'medium_dense' || state.coat === 'doublecoat' || state.problem === 'undercoat' || state.intensity === 'intensive') return 'Lila';
        return 'Gold';
      }
      return 'Gold';
    }

    function buildLink(prod, variantId) {
      const href = variantId ? (`/products/${prod.handle}?variant=${variantId}`) : (`/products/${prod.handle}`);
      const img = prod.featured_image ? (prod.featured_image.startsWith('http') ? prod.featured_image : ('https:' + prod.featured_image)) : '';
      return { href, img, title: prod.title || '', variantId: variantId || null };
    }

    function findVariantLink(productKey, size, hardness) {
      const prod = productsRaw[productKey];
      if (!prod || !prod.handle) return { href: '#', img: '', title: '', variantId: null };

      const variants = variantsRaw[productKey] || [];
      const sizeNeed = (size === '9') ? '9 cm' : '4.5 cm';

      if (productKey === 'daily_care' && hardness) {
        const match = variants.find(v => {
          const opts = [v.option1, v.option2, v.option3].map(o => normalize(o || ''));
          const okSize = opts.includes(normalize(sizeNeed));
          const okHardness =
            (hardness === 'Grün' && opts.includes('grün')) ||
            (hardness === 'Gold' && opts.includes('gold')) ||
            (hardness === 'Lila' && opts.includes('lila'));
          return okSize && okHardness;
        });
        if (match) return buildLink(prod, match.id);
      }

      const bySize = variants.find(v => normalize(v.title).includes(normalize(sizeNeed)));
      return buildLink(prod, bySize ? bySize.id : null);
    }

    function formatMoneyCents(cents) {
      try {
        const v = (Number(cents || 0) / 100);
        return v.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
      } catch (e) {
        const v = (Number(cents || 0) / 100).toFixed(2).replace('.', ',');
        return v + ' €';
      }
    }

    function setInventory(el, variant) {
      if (!el || !variant) return;

      if (variant.available) {
        const q = (typeof variant.inventory_quantity === 'number') ? variant.inventory_quantity : null;
        if (q !== null && q >= 0 && q < 5) {
          el.textContent = "Nur noch wenige verfügbar";
          el.className = "psqC__inventory low";
        } else {
          el.textContent = "Sofort verfügbar";
          el.className = "psqC__inventory instock";
        }
      } else {
        el.textContent = "Aktuell ausverkauft";
        el.className = "psqC__inventory out";
      }
    }

    function scoreAll() {
      const keys = Object.keys(BRUSH);
      const s = {}; keys.forEach(k => s[k] = 0);

      if (state.coat === 'short_smooth') {
        s.pro_weich += 4; s.daily_care += 4; s.cg_supersoft += 4; s.duo_silcoater += 3;
        s.matt_zapper -= 5; s.duo_tuffzapper -= 4; s.cg_stark -= 4; s.duo_tufffinish -= 2; s.undercoater_duo -= 3; s.pro_stark -= 2;
      }
      if (state.coat === 'silky') {
        s.pro_weich += 4; s.daily_care += 4; s.cg_supersoft += 4; s.duo_silcoater += 4;
        s.matt_zapper -= 5; s.duo_tuffzapper -= 4; s.pro_stark -= 3; s.cg_stark -= 4; s.undercoater_duo -= 3;
      }
      if (state.coat === 'medium_dense') {
        s.pro_stark += 4; s.duo_tufffinish += 3; s.cg_stark += 3; s.undercoater_duo += 3; s.duo_tuffzapper += 1;
        s.daily_care -= 2; s.pro_weich -= 2; s.cg_supersoft -= 3; s.duo_silcoater -= 2;
      }
      if (state.coat === 'curly') {
        s.matt_zapper += 5; s.duo_tuffzapper += 5; s.undercoater_duo += 4; s.pro_stark += 3; s.duo_tufffinish += 2;
        s.daily_care -= 4; s.pro_weich -= 4; s.cg_supersoft -= 5; s.duo_silcoater -= 4; s.cg_stark += 1;
      }
      if (state.coat === 'doublecoat') {
        s.cg_stark += 5; s.undercoater_duo += 5; s.pro_stark += 4; s.duo_tufffinish += 3; s.duo_tuffzapper += 3; s.matt_zapper += 2;
        s.daily_care -= 3; s.pro_weich -= 2; s.cg_supersoft -= 3; s.duo_silcoater -= 2;
      }

      if (state.problem === 'mats_strong') {
        s.matt_zapper += 7; s.duo_tuffzapper += 7; s.undercoater_duo += 5;
        s.pro_stark += 1; s.duo_tufffinish -= 2; s.cg_stark -= 2;
        s.daily_care -= 8; s.pro_weich -= 8; s.cg_supersoft -= 6; s.duo_silcoater -= 6;
      }
      if (state.problem === 'mats_light') {
        s.matt_zapper += 3; s.pro_stark += 3; s.undercoater_duo += 3; s.duo_tufffinish += 2; s.duo_tuffzapper += 3;
        s.daily_care -= 1; s.pro_weich -= 1; s.cg_supersoft -= 1; s.duo_silcoater -= 1;
      }
      if (state.problem === 'undercoat') {
        s.cg_stark += 6; s.undercoater_duo += 6; s.pro_stark += 4; s.duo_tufffinish += 3; s.duo_tuffzapper += 2;
        s.daily_care -= 3; s.pro_weich -= 3;
        if (state.coat === 'short_smooth' || state.coat === 'silky') {
          s.cg_supersoft += 6; s.duo_silcoater += 2; s.cg_stark -= 4; s.undercoater_duo -= 2;
        }
      }
      if (state.problem === 'shedding') {
        s.daily_care += 5; s.pro_weich += 5; s.duo_silcoater += 4; s.cg_supersoft += 2;
        s.pro_stark += 1; s.duo_tufffinish += 1;
        s.matt_zapper -= 4; s.duo_tuffzapper -= 4;
      }
      if (state.problem === 'routine') {
        s.daily_care += 6; s.pro_weich += 6; s.duo_silcoater += 6; s.cg_supersoft += 2;
        s.matt_zapper -= 5; s.duo_tuffzapper -= 5; s.cg_stark -= 1; s.undercoater_duo -= 2;
      }

      if (state.intensity === 'gentle') {
        s.daily_care += 4; s.pro_weich += 4; s.cg_supersoft += 3; s.duo_silcoater += 4;
        s.matt_zapper -= 5; s.duo_tuffzapper -= 5; s.pro_stark -= 2; s.cg_stark -= 2; s.undercoater_duo -= 2; s.duo_tufffinish -= 1;
      }
      if (state.intensity === 'regular') {
        s.pro_stark += 3; s.duo_tufffinish += 3; s.cg_stark += 2; s.daily_care += 1; s.pro_weich += 1; s.undercoater_duo += 1;
        s.matt_zapper -= 1;
      }
      if (state.intensity === 'intensive') {
        s.matt_zapper += 5; s.duo_tuffzapper += 5; s.undercoater_duo += 4; s.pro_stark += 2;
        s.daily_care -= 4; s.pro_weich -= 4; s.duo_silcoater -= 3; s.cg_supersoft -= 3;
      }

      if (state.skin === 'sensitive') {
        s.daily_care += 6; s.pro_weich += 6; s.cg_supersoft += 5; s.duo_silcoater += 5;
        s.pro_stark -= 5; s.cg_stark -= 5; s.matt_zapper -= 6; s.duo_tuffzapper -= 6; s.undercoater_duo -= 4; s.duo_tufffinish -= 3;
      }
      if (state.skin === 'robust') {
        s.pro_stark += 2; s.cg_stark += 2; s.duo_tufffinish += 2;
        if (state.problem === 'mats_strong') s.matt_zapper += 1;
        if (state.problem === 'undercoat') s.undercoater_duo += 1;
      }

      if (state.problem === 'mats_strong' && state.skin === 'sensitive') {
        s.duo_tuffzapper -= 2;
        s.matt_zapper -= 2;
        s.undercoater_duo += 1;
      }

      return s;
    }

    function resolveInStockCandidates(size, hardness) {
      const score = scoreAll();

      const ranked = Object.entries(score)
        .map(([key, val]) => ({ key, score: val }))
        .sort((a, b) => b.score - a.score);

      const candidates = [];
      for (const item of ranked) {
        const brush = BRUSH[item.key];
        if (!brush) continue;

        const link = findVariantLink(brush.key, size, (brush.key === 'daily_care' ? hardness : null));
        const vId = link.variantId;
        if (!vId) continue;

        const variants = variantsRaw[brush.key] || [];
        const v = variants.find(x => x && x.id === vId);
        if (v && v.available) {
          candidates.push({ brush, link, variant: v, score: item.score });
        }
      }
      return candidates;
    }

    function makeProfile() {
      const coatMap = {
        short_smooth: "Kurzes, glattes Fell",
        silky: "Seidiges, feines Fell",
        medium_dense: "Mittellanges, dichtes Fell",
        curly: "Lockige, stark strukturierte Fellform",
        doublecoat: "Dichtes Doppelfell mit Unterwolle"
      };
      const probMap = {
        mats_strong: "mit ausgeprägter Verfilzungsneigung",
        mats_light: "mit ersten Knoten oder leichten Verfilzungen",
        undercoat: "mit deutlich spürbarer Unterwolle (Fellwechsel)",
        shedding: "mit vielen losen Haaren im Alltag",
        routine: "für regelmäßige, schonende Pflege"
      };

      let end = "";
      if (state.intensity === 'gentle') end = "Am besten funktioniert hier eine sanfte Routine, die das Fell konstant gepflegt hält – ohne Ziepen.";
      if (state.intensity === 'regular') end = "Ideal ist eine regelmäßige, gründliche Pflege, die Deckhaar und Unterfell sauber in Balance bringt.";
      if (state.intensity === 'intensive') end = "Hier lohnt sich eine intensive Lösung, die Knoten löst und die Fellstruktur wieder durchlässig macht.";

      let skin = "";
      if (state.skin === 'sensitive') skin = "Da die Haut empfindlich ist, setzen wir auf besonders schonende Werkzeuge.";
      if (state.skin === 'robust') skin = "Bei robuster Haut darf die Bürste effektiver arbeiten, ohne unnötig zu reizen.";

      return `${coatMap[state.coat] || "Individuelle Fellstruktur"} ${probMap[state.problem] || ""}. ${end} ${skin}`.trim();
    }

    function byId(id) {
      return root.querySelector(`#${CSS.escape(id)}`);
    }

    function renderCard(which, brushObj, linkObj, variantObj, sizeTxt, hardness) {
      const isMain = which === 'main';

      const titleEl = byId(isMain ? 'psqMainTitle' : 'psqAltTitle');
      const descEl = byId(isMain ? 'psqMainDesc' : 'psqAltDesc');
      const imgEl = byId(isMain ? 'psqMainImg' : 'psqAltImg');
      const metaEl = byId(isMain ? 'psqMainMeta' : 'psqAltMeta');
      const linkEl = byId(isMain ? 'psqMainLink' : 'psqAltLink');
      const addBtn = byId(isMain ? 'psqMainAdd' : 'psqAltAdd');
      const priceEl = byId(isMain ? 'psqMainPrice' : 'psqAltPrice');
      const invEl = byId(isMain ? 'psqMainInventory' : 'psqAltInventory');

      if (imgEl) {
        imgEl.src = linkObj.img || '';
        imgEl.alt = brushObj.title;
      }
      if (titleEl) titleEl.textContent = brushObj.title + (brushObj.key === 'daily_care' ? ` – ${hardness}` : '');
      if (descEl) descEl.textContent = brushObj.desc || '';
      if (metaEl) metaEl.textContent = sizeTxt || '';
      if (linkEl) linkEl.href = linkObj.href || '#';

      // ✅ Clickable title + image ALWAYS uses CURRENT href (no stale closure)
      const currentHref = (linkObj && linkObj.href) ? linkObj.href : null;

      const bindClickNav = (el) => {
        if (!el) return;

        if (currentHref) el.dataset.psqHref = currentHref;
        el.style.cursor = currentHref ? 'pointer' : '';

        if (el.dataset.psqNavBound === '1') return;
        el.dataset.psqNavBound = '1';

        el.addEventListener('click', (ev) => {
          const a = ev.target && ev.target.closest ? ev.target.closest('a') : null;
          if (a) return;

          const href = el.dataset.psqHref;
          if (!href) return;

          window.location.href = href;
        });
      };

      bindClickNav(imgEl);
      bindClickNav(titleEl);

      if (isMain) {
        const bulletsEl = byId('psqMainBullets');
        if (bulletsEl) {
          bulletsEl.innerHTML = '';
          (brushObj.bullets || []).forEach(t => {
            const li = document.createElement('li');
            li.textContent = t;
            bulletsEl.appendChild(li);
          });
        }
      }

      // ✅ CHANGE: Compare-at price (Streichpreis) anzeigen, wenn vorhanden & > price
      // ✅ MwSt. bleibt darunter
      if (priceEl) {
        if (variantObj) {
          const price = Number(variantObj.price || 0);

          // Shopify variant JSON usually: compare_at_price
          // Some themes/custom exports might use compare_at_price_cents
          const capRaw =
            (variantObj.compare_at_price != null ? variantObj.compare_at_price : null) ??
            (variantObj.compare_at_price_cents != null ? variantObj.compare_at_price_cents : null);

          const compareAt = (capRaw == null) ? 0 : Number(capRaw || 0);

          const showCompare = Number.isFinite(compareAt) && compareAt > 0 && Number.isFinite(price) && compareAt > price;

          priceEl.innerHTML = `
            ${showCompare ? `<span class="psqC__compareAt" style="display:inline-block;text-decoration:line-through;opacity:.6;margin-right:.5rem;">${formatMoneyCents(compareAt)}</span>` : ``}
            <span class="psqC__priceValue">${formatMoneyCents(price)}</span>
            <span class="psqC__vat">inkl. MwSt.</span>
          `;
        } else {
          priceEl.textContent = '—';
        }
      }

      if (invEl) {
        if (variantObj) setInventory(invEl, variantObj);
        else { invEl.textContent = '—'; invEl.className = 'psqC__inventory'; }
      }

      if (addBtn) {
        const vId = linkObj.variantId;
        addBtn.disabled = !(variantObj && variantObj.available && vId);

        const original = (addBtn.getAttribute('data-psq-label') || addBtn.textContent || 'In den Warenkorb').trim();
        addBtn.setAttribute('data-psq-label', original);

        let busy = false;

        addBtn.onclick = async () => {
          if (!vId || busy) return;
          busy = true;

          addBtn.textContent = 'Hinzugefügt ✓';
          addBtn.disabled = true;

          try {
            await addToCartAndUpdateDrawer(vId);
            window.setTimeout(() => {
              if (document.body.contains(addBtn)) addBtn.textContent = original;
            }, 900);
          } catch (e) {
            window.setTimeout(() => {
              if (document.body.contains(addBtn)) addBtn.textContent = original;
            }, 900);
          } finally {
            addBtn.disabled = false;
            busy = false;
          }
        };
      }
    }

    function renderResult() {
      const size = inferSize();
      const hardness = dailyHardness();

      const sizeLabel = (size === '9') ? '9 cm – effizient für große Fellflächen' : '4,5 cm – präzise & kontrolliert';
      const careLabel =
        (state.intensity === 'gentle') ? 'Sanft' :
          (state.intensity === 'regular') ? 'Regelmäßig' : 'Intensiv';
      const skinLabel =
        (state.skin === 'sensitive') ? 'Empfindlich' :
          (state.skin === 'normal') ? 'Normal' : 'Robust';

      const pillSize = byId('psqPillSize');
      const pillCare = byId('psqPillCare');
      const pillSkin = byId('psqPillSkin');
      if (pillSize) pillSize.textContent = `Größe: ${sizeLabel}`;
      if (pillCare) pillCare.textContent = `Pflege: ${careLabel}`;
      if (pillSkin) pillSkin.textContent = `Haut: ${skinLabel}`;

      const sizeTxt = (size === '9')
        ? "Empfohlen: 9 cm – ideal für große Fellflächen und schnelleres Arbeiten."
        : "Empfohlen: 4,5 cm – präzise Führung für kleinere oder sensible Bereiche.";

      const profile = byId('psqProfile');
      if (profile) profile.textContent = makeProfile();

      const notice = byId('psqStockNotice');
      const mainWrap = byId('psqMain');
      const altWrap = byId('psqAltWrap');

      const candidates = resolveInStockCandidates(size, hardness);

      if (!candidates.length) {
        if (notice) notice.hidden = false;
        if (mainWrap) mainWrap.hidden = true;
        if (altWrap) altWrap.hidden = true;
        return;
      }

      if (notice) notice.hidden = true;
      if (mainWrap) mainWrap.hidden = false;

      const main = candidates[0];
      const alt = candidates.length > 1 ? candidates[1] : null;

      renderCard('main', main.brush, main.link, main.variant, sizeTxt, hardness);

      const mainTip = byId('psqMainTip');
      if (mainTip) {
        if (state.problem === 'mats_strong') mainTip.textContent = "Tipp: Bei starken Verfilzungen immer in kleinen Sektionen arbeiten – ohne Druck, lieber mehrere Durchgänge.";
        else if (state.problem === 'undercoat') mainTip.textContent = "Tipp: Für Unterwolle funktioniert kurze, gleichmäßige Führung am besten – arbeite mit dem Fellverlauf.";
        else if (state.intensity === 'gentle') mainTip.textContent = "Tipp: Für sanfte Routine reichen kurze Sessions – lieber regelmäßig als selten und zu intensiv.";
        else mainTip.textContent = "Tipp: Arbeite in kurzen Sektionen und ohne Druck – so bleibt die Pflege angenehm und kontrolliert.";
      }

      const altTip = byId('psqAltTip');
      if (alt) {
        renderCard('alt', alt.brush, alt.link, alt.variant, sizeTxt, hardness);
        if (altTip) {
          if (state.skin === 'sensitive') altTip.textContent = "Alternative eignet sich besonders, wenn du die Pflege noch sanfter und hautschonender halten möchtest.";
          else altTip.textContent = "Alternative eignet sich besonders, wenn du die Pflege etwas sanfter oder alltagstauglicher halten möchtest.";
        }
        if (altWrap) altWrap.hidden = false;
      } else {
        if (altWrap) altWrap.hidden = true;
      }
    }

    function showHero(doScroll = false) {
      showPanel(root.querySelector('[data-view="hero"]'), doScroll);
      if (sticky) sticky.hidden = true;
      if (bar) bar.style.width = '0%';
      if (stickyStep) stickyStep.textContent = '1/5';
      stepIdx = -1;
    }

    root.addEventListener('click', (e) => {
      const btn = e.target.closest('button, a');
      if (!btn) return;

      const act = btn.dataset.act;

      if (act === 'start') { showStep(0); return; }
      if (act === 'jumpInfo') { if (sticky) sticky.hidden = true; showPanel(root.querySelector('[data-view="info"]'), true); return; }
      if (act === 'backHero') { showHero(true); return; }

      if (act === 'next') {
        const cur = steps[stepIdx];
        const ok = validateStep(cur);
        if (!ok) { showErr(cur, true); return; }
        showStep(Math.min(stepIdx + 1, 4));
        return;
      }

      if (act === 'back') { showStep(Math.max(stepIdx - 1, 0)); return; }

      if (act === 'result') {
        const ok = validateStep('5');
        if (!ok) { showErr('5', true); return; }
        renderResult();
        showStep(5);
        return;
      }

      if (act === 'restart') {
        Object.keys(state).forEach(k => state[k] = null);
        root.querySelectorAll('[aria-pressed]').forEach(x => x.setAttribute('aria-pressed', 'false'));
        const dogSize = byId('psqDogSize');
        if (dogSize) dogSize.hidden = true;
        ['1', '2', '3', '4', '5'].forEach(s => setNextDisabled(s, true));
        showHero(true);
        return;
      }

      const q = btn.dataset.q;
      const v = btn.dataset.v;
      if (q && v) {
        state[q] = v;

        if (q === 'animal') {
          setPressed('.psq__card[data-q="animal"]', v);
          const dogSize = byId('psqDogSize');
          if (dogSize) {
            if (v === 'dog') { dogSize.hidden = false; }
            else {
              dogSize.hidden = true;
              state.size = null;
              setPressed('.psq__chip[data-q="size"]', null);
            }
          }
        }
        if (q === 'size') setPressed('.psq__chip[data-q="size"]', v);
        if (q === 'coat') setPressed('.psq__card[data-q="coat"]', v);
        if (q === 'problem') setPressed('.psq__card[data-q="problem"]', v);
        if (q === 'intensity') setPressed('.psq__card[data-q="intensity"]', v);
        if (q === 'skin') setPressed('.psq__card[data-q="skin"]', v);

        const cur = steps[stepIdx];
        if (cur) {
          showErr(cur, false);
          setNextDisabled(cur, !validateStep(cur));
        }
      }
    });

    // default start: hero (refresh always resets)
    showHero(false);
  });

  // ✅ Keep state ONLY on browser back/forward cache (bfcache); refresh resets naturally.
  window.addEventListener('pageshow', (e) => {
    if (!e.persisted) return;
    window.dispatchEvent(new Event('resize'));
  });
})();